/**
 * Copy de cara al cliente de Perrón Points, centralizado para permitir tests
 * verbatim (spec `docs/specs/perron-points-plan.md` → sección "Copy verbatim",
 * extendido por `perron-points-restriccion-productos-plan.md` → R3). Cubre Web
 * F1 (saldo en el header, modal de canje, checkout), Web F2 (precompra Early
 * Access) y la restricción de productos por umbral propio.
 *
 * Regla: ningún texto de cara al cliente debe vivir hardcoded en plantillas o
 * componentes — todo pasa por aquí.
 *
 * Todo `{umbral}` se interpola con `formatThousands` (separador de espacio) vía
 * los resolvers `vipBadgeText` / `requirementHintText` / … de este mismo
 * archivo: no hay un segundo formato numérico. Los textos con variante de
 * umbral `0` viven en pares (`x` / `xRegistered`) y el resolver elige.
 */
export const PERRON_POINTS_COPY = {
  common: {
    loadError:
      'No pudimos consultar tus Perrón Points. Inténtalo de nuevo en unos minutos.',
  },

  /** Bloque logueado del dropdown de usuario (header). */
  header: {
    /** Lleva el placeholder `{n}` (saldo actual). */
    balance: '{n} Perrón Points',
    tierActive: 'Acceso anticipado activo',
    /** Lleva el placeholder `{n}` (PP que faltan para el umbral). */
    tierPending: 'Te faltan {n} PP para el acceso anticipado',
    cta: 'Registrar Perrón Points',
  },

  /** Modal de canje de código, montado una única vez en `app.ts`. */
  redeemModal: {
    title: 'Registrar Perrón Points',
    intro: 'Escribí tu código y sumá Perrón Points a tu cuenta.',
    inputLabel: 'Código',
    inputPlaceholder: 'XXXX-XXXX-XXXX',
    cta: 'Canjear código',
    /** Lleva los placeholders `{n}` (PP sumados) y `{m}` (nuevo saldo). */
    success: '¡Listo! Sumaste {n} Perrón Points. Nuevo saldo: {m} PP.',
    /** Prefijo visual del mensaje de éxito (ver `successMessage()` del modal). */
    successPrefix: '✓',
    /** 409 — código ya consumido. */
    consumed: 'Este código ya fue canjeado.',
    /** 422 — código inválido, inexistente o anulado. */
    invalid: 'El código no es válido. Revisá que esté bien escrito.',
    /** 429 — rate-limiting del canje. */
    throttled: 'Hiciste demasiados intentos. Esperá un momento y volvé a intentarlo.',
    /** Fallo técnico genérico (red, 5xx, etc.). */
    error: 'No pudimos canjear el código. Intentalo de nuevo en unos minutos.',
    cancel: 'Cancelar',
    closeAriaLabel: 'Cerrar',
  },

  /** Bloque del resumen de carrito y del checkout. */
  checkout: {
    /** Chip por línea. Lleva el placeholder `{n}`. */
    lineChip: '+{n} PP',
    /** Fila autoritativa del total. Lleva el placeholder `{n}`. */
    totalRow: 'Perrón Points de esta compra: {n} PP',
    /** Aclaración, siempre visible junto a la fila del total. */
    clarification: 'Se acreditan al confirmarse tu pago. El envío no suma puntos.',
    /** Proyección de saldo (logueado). Lleva `{a}` (saldo actual) y `{b}` (saldo tras la compra). */
    projection: 'Tu saldo pasará de {a} a {b} PP.',
    /** Lleva el placeholder `{n}` (PP que faltarán tras esta compra). */
    tierMissing: 'Te faltarán {n} PP para el acceso anticipado.',
    tierReached: 'Con esta compra alcanzás el acceso anticipado.',
    /** Banner de invitación a login (invitación, jamás bloqueo). Lleva `{n}`. */
    guestBanner: 'Iniciá sesión antes de finalizar tu compra y ganá {n} Perrón Points.',
    guestBannerCta: 'Iniciar sesión',
  },

  /** Precompra Early Access (modal de precompra, 3 variantes según sesión/saldo). */
  earlyAccess: {
    badge: 'Acceso anticipado',
    /**
     * Badge de identidad VIP, el mismo en las cinco superficies (card, listado,
     * zoom, ficha y modal de precompra). Desde R3 ocupa SLOT PROPIO: ya no
     * sustituye a «Pre-venta», de ahí que desaparezca el prefijo «PV - ». Lleva
     * `{umbral}`; para umbral `0` manda `vipBadgeRegistered` (ver `vipBadgeText`).
     */
    vipBadge: 'Acceso VIP · {umbral}+ PP',
    /** Variante de umbral `0` del badge: basta con tener cuenta iniciada (R5). */
    vipBadgeRegistered: 'Acceso VIP · Solo registrados',
    /**
     * Subtexto de requisito bajo el badge/título en la ficha de producto.
     * Lleva el placeholder `{umbral}`, formateado con separador de miles
     * (ver `formatThousands`/`withRequirementHint`).
     */
    requirementHint: 'Exclusivo para clientes con {umbral}+ Perrón Points',
    /** Variante de umbral `0` del requisito de la ficha. */
    requirementHintRegistered: 'Exclusivo para clientes registrados',
    /** Sin sesión. Lleva el placeholder `{umbral}`. */
    guestLocked:
      'Esta precompra es exclusiva para clientes con acceso anticipado ({umbral} Perrón Points). Iniciá sesión para verificar tu nivel.',
    guestLockedCta: 'Iniciar sesión',
    loadingLocked: 'Estamos verificando tus Perrón Points…',
    unavailableLocked:
      'No pudimos verificar tus Perrón Points. Reintenta antes de continuar.',
    retryCta: 'Reintentar',
    /** Con sesión pero saldo insuficiente. Lleva `{umbral}` y `{n}` (saldo actual). */
    insufficientLocked:
      'Necesitás {umbral} Perrón Points para esta precompra exclusiva y tenés {n}. Sumá puntos con tus compras (1 PP por cada ₡100) o canjeando códigos.',
    /**
     * Fallback ESTÁTICO (sin interpolar) de `ERROR_WORDING` en el modal de
     * precompra: solo se ve si el 403 EA llega sin `err.message` (defensa en
     * profundidad); en el camino normal manda `guestLocked`/`insufficientLocked`
     * ya interpolados. Voseo, igual que el resto del copy PP.
     */
    guestLockedFallback: 'Iniciá sesión para verificar tu acceso anticipado.',
    insufficientLockedFallback: 'No tenés suficientes Perrón Points para esta precompra exclusiva.',
    /**
     * Requisito visible en cada card EA de la lista de precompras (sin hover,
     * también para invitados). Lleva `{umbral}`, formateado con separador de
     * miles (ver `withListRequirement`). Distinto de `requirementHint` (ficha)
     * solo en la palabra inicial, para calzar mejor en el espacio de una card.
     */
    listRequirement: 'Requiere {umbral}+ Perrón Points',
    /** Variante de umbral `0` del requisito de la card. */
    listRequirementRegistered: 'Requiere cuenta registrada',
    /**
     * Mensaje de celebración cuando el gate del modal de precompra resuelve
     * `eaVariant() === 'granted'` (saldo suficiente). Único lugar, junto al
     * éxito de canje, donde aparece la cara corgi de la moneda.
     */
    grantedMessage: '¡Acceso VIP desbloqueado! Ya podés reservar esta precompra.',
  },

  /**
   * Diálogo bloqueante de «Agregar al carrito» (R2): la vía primaria de corte,
   * antes de que el producto entre al carrito. Cuatro variantes según sesión y
   * estado de la verificación del saldo.
   */
  blockedDialog: {
    title: 'Producto de Acceso VIP',
    /** Invitado, umbral `N ≥ 1`. Lleva `{umbral}`. */
    guest:
      'Este producto es exclusivo para clientes con {umbral}+ Perrón Points. Iniciá sesión para verificar tu saldo.',
    /** Invitado, umbral `0`: basta con registrarse. */
    guestRegistered:
      'Este producto es exclusivo para clientes registrados. Iniciá sesión para agregarlo a tu carrito.',
    guestCta: 'Iniciar sesión',
    /** Con sesión y saldo insuficiente. Lleva `{umbral}` y `{n}` (saldo actual). */
    insufficient:
      'Necesitás {umbral} Perrón Points para llevarte este producto y tenés {n}. Sumá puntos con tus compras (1 PP por cada ₡100) o canjeando códigos.',
    /** CTA de la variante con sesión: abre el modal de canje existente. */
    insufficientCta: 'Registrar Perrón Points',
    /** CTA secundario, presente en todas las variantes: cierra sin agregar. */
    secondaryCta: 'Seguir viendo',
    loading: 'Estamos verificando tus Perrón Points…',
    error: 'No pudimos verificar tus Perrón Points. Volvé a intentarlo.',
    errorCta: 'Reintentar',
  },

  /**
   * Línea de carrito bloqueada (R4) y su aviso de cabecera. El mismo copy
   * cubre el caso de cerrar sesión con la línea dentro (R6): el estado se
   * deriva en lectura, no hay diálogo especial al salir.
   */
  blockedLine: {
    label: 'Bloqueado',
    /** Motivo, umbral `N ≥ 1`. Lleva `{umbral}`. */
    reason: 'Requiere {umbral}+ Perrón Points para comprarse.',
    /** Motivo, umbral `0`. */
    reasonRegistered: 'Requiere iniciar sesión para comprarse.',
    removeCta: 'Quitar del carrito',
    /**
     * Aviso de cabecera del carrito y del checkout. DOS variantes porque la
     * salida es distinta: el invitado puede desbloquear iniciando sesión, el
     * cliente con sesión y saldo corto solo puede quitar las líneas.
     */
    noticeGuest:
      'Tenés productos de Acceso VIP en tu carrito. Iniciá sesión o quitalos para continuar.',
    noticeAuthenticated:
      'Tenés productos de Acceso VIP que todavía no podés comprar. Quitalos del carrito para continuar.',
  },

  /**
   * Mapeo del 403 del gate en carrito y checkout (`early-access-gate.ts`).
   * `singleLine` sustituye al mensaje genérico cuando el 403 identifica UNA
   * sola línea bloqueada, que es el caso mayoritario.
   */
  gateError: {
    loginRequired: 'Iniciá sesión para comprar los productos de Acceso VIP de tu carrito.',
    loginRequiredCta: 'Iniciar sesión',
    insufficientPoints:
      'Te faltan Perrón Points para los productos de Acceso VIP de tu carrito. Quitalos o sumá puntos para continuar.',
    /** Una sola línea bloqueada. Lleva `{producto}` y `{umbral}`. */
    singleLine: '“{producto}” requiere {umbral}+ Perrón Points.',
    /** 403 sin `error` reconocible (API antigua o code inesperado). */
    fallback:
      'No pudimos verificar tu acceso a este producto. Intentalo de nuevo en unos minutos.',
  },
} as const;

/** Sustituye el placeholder `{n}` por el número dado (saldo, PP de una línea, etc.). */
export function withPoints(text: string, n: number): string {
  return text.replace('{n}', String(n));
}

/** Sustituye `{a}` y `{b}` de la proyección de saldo ("Tu saldo pasará de {a} a {b} PP."). */
export function withBalance(text: string, a: number, b: number): string {
  return text.replace('{a}', String(a)).replace('{b}', String(b));
}

/**
 * Sustituye el placeholder `{n}` de los textos "te faltan N PP" (header y
 * checkout). Implementación idéntica a `withPoints`, pero nombrada aparte
 * porque semánticamente interpola un "faltante", no un saldo o total.
 */
export function withMissing(text: string, n: number): string {
  return text.replace('{n}', String(n));
}

/**
 * Interpola el mensaje de éxito del canje, el único texto con DOS
 * placeholders (`{n}` PP sumados, `{m}` nuevo saldo).
 */
export function withRedeemSuccess(text: string, points: number, balance: number): string {
  return text.replace('{n}', String(points)).replace('{m}', String(balance));
}

/** Sustituye el placeholder `{umbral}` (umbral de Early Access). */
export function withThreshold(text: string, umbral: number): string {
  return text.replace('{umbral}', String(umbral));
}

/** Formatea un entero con separador de miles (espacio), p. ej. 2000 → "2 000". */
export function formatThousands(n: number): string {
  return Math.trunc(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** Sustituye `{umbral}` en `requirementHint`, formateando el número con separador de miles. */
export function withRequirementHint(text: string, umbral: number): string {
  return text.replace('{umbral}', formatThousands(umbral));
}

/** Sustituye `{umbral}` en `listRequirement` (card de precompra), con separador de miles. */
export function withListRequirement(text: string, umbral: number): string {
  return text.replace('{umbral}', formatThousands(umbral));
}

/**
 * Interpola la variante "sin puntos" de Early Access, con DOS placeholders
 * (`{umbral}` y `{n}`, saldo actual del usuario).
 */
export function withEarlyAccessInsufficient(text: string, umbral: number, balance: number): string {
  return text.replace('{umbral}', String(umbral)).replace('{n}', String(balance));
}

/* ─────────────────────────────────────────────────────────────────────────
 * Resolvers de umbral (R3/R5)
 *
 * Cada texto con requisito tiene DOS variantes: la de umbral `N ≥ 1` (con
 * `{umbral}` interpolado y formateado) y la de umbral `0` (solo requiere
 * sesión). El corte es siempre `umbral <= 0`, nunca un booleano aparte, para
 * que un `0` que llega del API no se lea como "hereda el global" (trampa del
 * `??` vs `?:` de R5, en su versión de front).
 * ───────────────────────────────────────────────────────────────────────── */

/** Badge de identidad VIP, la misma en card, listado, zoom, ficha y modal. */
export function vipBadgeText(umbral: number): string {
  const ea = PERRON_POINTS_COPY.earlyAccess;
  return umbral <= 0 ? ea.vipBadgeRegistered : withRequirementHint(ea.vipBadge, umbral);
}

/** Subtexto de requisito de la ficha de producto. */
export function requirementHintText(umbral: number): string {
  const ea = PERRON_POINTS_COPY.earlyAccess;
  return umbral <= 0
    ? ea.requirementHintRegistered
    : withRequirementHint(ea.requirementHint, umbral);
}

/** Requisito visible en la card de listado. */
export function listRequirementText(umbral: number): string {
  const ea = PERRON_POINTS_COPY.earlyAccess;
  return umbral <= 0
    ? ea.listRequirementRegistered
    : withListRequirement(ea.listRequirement, umbral);
}

/** Cuerpo del diálogo de bloqueo en la variante invitado. */
export function blockedDialogGuestText(umbral: number): string {
  const d = PERRON_POINTS_COPY.blockedDialog;
  return umbral <= 0 ? d.guestRegistered : withRequirementHint(d.guest, umbral);
}

/**
 * Cuerpo del diálogo de bloqueo con sesión y saldo corto. Un producto de
 * umbral `0` JAMÁS llega aquí (R5: cualquier usuario logueado pasa), pero se
 * deja el mismo corte por defensa en profundidad.
 */
export function blockedDialogInsufficientText(umbral: number, balance: number): string {
  const d = PERRON_POINTS_COPY.blockedDialog;
  if (umbral <= 0) return d.guestRegistered;
  return withRequirementHint(d.insufficient, umbral).replace('{n}', String(balance));
}

/** Motivo del bloqueo en una línea de carrito. */
export function blockedLineReasonText(umbral: number): string {
  const l = PERRON_POINTS_COPY.blockedLine;
  return umbral <= 0 ? l.reasonRegistered : withRequirementHint(l.reason, umbral);
}

/** Mensaje personalizado del 403 cuando hay UNA sola línea bloqueada. */
export function gateErrorSingleLineText(producto: string, umbral: number): string {
  return withRequirementHint(PERRON_POINTS_COPY.gateError.singleLine, umbral).replace(
    '{producto}',
    producto,
  );
}
